import { Controller } from "@hotwired/stimulus"

// Shared offline mode preference selector for PWA apps.
//
// Manages the three-button mode selector (auto/online_only/manual) on settings pages.
// Persists to localStorage immediately and PATCHes the server for cross-device roaming.
//
// Stimulus values:
//   prefix   -- localStorage/event namespace (e.g. "field", "chat")
//   saveUrl  -- server endpoint for cross-device roaming (PATCH)
//   current  -- server-authoritative mode on page load

export default class extends Controller {
  static targets = ["option"]
  static values  = { prefix: String, saveUrl: String, current: String }

  connect() {
    const mode = this.currentValue || "auto"
    localStorage.setItem(`${this.prefixValue}:offline_mode`, mode)
    this._highlightActive(mode)
  }

  select(event) {
    const mode = event.params.option
    if (!mode) return

    localStorage.setItem(`${this.prefixValue}:offline_mode`, mode)
    this._highlightActive(mode)

    // Notify the network status controller so it can adjust without a reload
    window.dispatchEvent(new CustomEvent(`${this.prefixValue}:offline-mode-changed`, {
      bubbles: true,
      detail: { mode }
    }))

    // Persist to server (fire-and-forget)
    if (this.hasSaveUrlValue && this.saveUrlValue) {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content
      fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: `field_offline_mode=${encodeURIComponent(mode)}`
      }).catch(() => {})
    }
  }

  _highlightActive(mode) {
    // Build the dataset key from the controller identifier.
    // e.g. "field-offline-mode" → "fieldOfflineModeOptionParam"
    const prefix = this.identifier.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
    const paramKey = `${prefix}OptionParam`

    this.optionTargets.forEach(btn => {
      const isActive = btn.dataset[paramKey] === mode
      if (isActive) {
        btn.classList.add("bg-white", "shadow-sm", "text-slate-800")
        btn.classList.remove("text-slate-500")
      } else {
        btn.classList.remove("bg-white", "shadow-sm", "text-slate-800")
        btn.classList.add("text-slate-500")
      }
    })
  }
}
